import { semantic } from '@diligentcorp/atlas-design-tokens/lens';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DesktopDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import {
  CUSTOM_FIELD_TYPE,
  CustomFieldFilterOption,
  DisclosureFilterColumn,
  DisclosureFilterValue,
  FilterOptionsByColumn,
} from 'src/components/pages/CoiDisclosures/types';
import {
  FilterPopover,
  FilterPopoverRow,
} from '../../molecules/FilterPopover/FilterPopover';
import * as styles from './DisclosuresFilterPopover.styles';

export type FilterRow = FilterPopoverRow<DisclosureFilterColumn>;

const COLUMN_OPTIONS_ALL: { value: DisclosureFilterColumn; label: string }[] = [
  { value: 'category', label: 'Category' },
  { value: 'status', label: 'Status' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'submittedDate', label: 'Submission Date' },
  { value: 'formTitle', label: 'Form Title' },
  { value: 'customField', label: 'Custom field' },
];

type DropdownColumn = 'category' | 'status' | 'reviewer';
type TextColumn = 'formTitle';

export interface DisclosuresFilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  rows: FilterPopoverRow<DisclosureFilterColumn>[];
  onRowsChange: (rows: FilterPopoverRow<DisclosureFilterColumn>[]) => void;
  value: DisclosureFilterValue;
  onChange: (value: DisclosureFilterValue) => void;
  onApply: (value: DisclosureFilterValue) => void;
  onRemoveAll: () => void;
  makeId: () => string;
  filterOptions: FilterOptionsByColumn;
  visibleColumns: DisclosureFilterColumn[];
}

export const DisclosuresFilterPopover: React.FC<
  DisclosuresFilterPopoverProps
> = ({
  anchorEl,
  open,
  onClose,
  rows,
  onRowsChange,
  value,
  onChange,
  onApply,
  onRemoveAll,
  makeId,
  filterOptions,
  visibleColumns,
}) => {
  const columnOptions = useMemo(
    () =>
      COLUMN_OPTIONS_ALL.filter((col) => visibleColumns.includes(col.value)),
    [visibleColumns],
  );

  const handleTextValueChange = (column: TextColumn, next: string) => {
    onChange({ ...value, [column]: next ? [next] : [] });
  };

  const handleDropdownChange = (column: DropdownColumn, next: string) => {
    onChange({ ...value, [column]: next ? [next] : [] });
  };

  const handleCustomFieldChange = (fieldId: string) => {
    if (!fieldId) {
      onChange(clearColumn(value, 'customField'));
      return;
    }
    onChange({ ...value, customField: { fieldId, value: '' } });
  };

  const handleCustomFieldValueChange = (fieldId: string, next: string) => {
    onChange({ ...value, customField: { fieldId, value: next } });
  };

  const handleDateRangeChange = (range: DateRange<Dayjs>) => {
    const [from, to] = range;
    if (!from && !to) {
      const next = { ...value };
      delete next.submittedDate;
      onChange(next);
      return;
    }
    onChange({
      ...value,
      submittedDate: {
        from: from ? from.format('YYYY-MM-DD') : '',
        to: to ? to.format('YYYY-MM-DD') : '',
      },
    });
  };

  const renderValueControl = (
    row: FilterPopoverRow<DisclosureFilterColumn>,
  ) => {
    if (!row.column) {
      return (
        <TextField
          size="small"
          disabled
          label="Value"
          placeholder="Filter value"
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'Filter value' }}
        />
      );
    }

    if (row.column === 'submittedDate') {
      const range = value.submittedDate;
      const dateRangeValue: DateRange<Dayjs> = [
        range?.from ? dayjs(range.from) : null,
        range?.to ? dayjs(range.to) : null,
      ];
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDateRangePicker
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            slotProps={{
              textField: ({ position }) => ({
                size: 'small',
                inputProps: {
                  'data-testid':
                    position === 'start'
                      ? 'disclosures-filter-date-from'
                      : 'disclosures-filter-date-to',
                },
              }),
              fieldSeparator: { children: '–' },
              day: {
                sx: {
                  '& .MuiPickersDay-root.Mui-selected': {
                    backgroundColor:
                      semantic.color.action.destructive.default.value,
                  },
                  '& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus':
                    {
                      backgroundColor:
                        semantic.color.action.destructive.hover.value,
                    },
                },
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    if (row.column === 'formTitle') {
      const text = value.formTitle?.[0] ?? '';
      return (
        <TextField
          size="small"
          label="Value"
          placeholder="Filter value"
          value={text}
          onChange={(e) => handleTextValueChange('formTitle', e.target.value)}
          sx={styles.field}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'disclosures-filter-value-formTitle' }}
        />
      );
    }

    if (row.column === 'customField') {
      const customFields = filterOptions.customField ?? [];
      const selection = value.customField;
      const selectedField = customFields.find(
        (field) => field.id === selection?.fieldId,
      );

      return (
        <>
          <FormControl size="small" sx={styles.field}>
            <InputLabel shrink>Field</InputLabel>
            <Select
              displayEmpty
              label="Field"
              value={selectedField?.id ?? ''}
              onChange={(e) => handleCustomFieldChange(e.target.value)}
              inputProps={{ 'aria-label': 'Filter custom field' }}
              SelectDisplayProps={
                {
                  'data-testid': 'disclosures-filter-custom-field',
                } as React.HTMLAttributes<HTMLDivElement>
              }
              renderValue={(selected) =>
                selected ? (
                  (customFields.find((field) => field.id === selected)?.label ??
                  selected)
                ) : (
                  <Typography component="span" sx={styles.placeholderText}>
                    Select a field
                  </Typography>
                )
              }
            >
              {customFields.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {renderCustomFieldValueControl(
            selectedField,
            selection?.value ?? '',
            handleCustomFieldValueChange,
          )}
        </>
      );
    }

    const column = row.column as DropdownColumn;
    const options = filterOptions[column] ?? [];
    const selectedValue = value[column]?.[0] ?? '';

    return renderLabeledValueSelect({
      ariaLabel: `Filter value for ${column}`,
      testId: `disclosures-filter-value-${column}`,
      options,
      selectedValue,
      onChange: (next) => handleDropdownChange(column, next),
    });
  };

  return (
    <FilterPopover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      rows={rows}
      onRowsChange={onRowsChange}
      value={value}
      onChange={onChange}
      onApply={(v) => onApply(v)}
      onRemoveAll={onRemoveAll}
      makeId={makeId}
      columnOptions={columnOptions}
      clearColumnValue={(col, val) => clearColumn(val, col)}
      renderValueControl={renderValueControl}
      testIdPrefix="disclosures"
      columnLabel="Columns"
    />
  );
};

function renderCustomFieldValueControl(
  field: CustomFieldFilterOption | undefined,
  selectedValue: string,
  onValueChange: (fieldId: string, next: string) => void,
) {
  if (!field) {
    return (
      <TextField
        size="small"
        disabled
        label="Value"
        placeholder="Filter value"
        sx={styles.field}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'aria-label': 'Filter value' }}
      />
    );
  }

  if (field.fieldType !== CUSTOM_FIELD_TYPE.SINGLE_SELECT) {
    return (
      <TextField
        size="small"
        label="Value"
        placeholder="Filter value"
        value={selectedValue}
        onChange={(e) => onValueChange(field.id, e.target.value)}
        sx={styles.field}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          'data-testid': 'disclosures-filter-value-customField',
        }}
      />
    );
  }

  return renderLabeledValueSelect({
    ariaLabel: `Filter value for ${field.label}`,
    testId: 'disclosures-filter-value-customField',
    options: field.options,
    selectedValue,
    onChange: (next) => onValueChange(field.id, next),
  });
}

function renderLabeledValueSelect({
  ariaLabel,
  testId,
  options,
  selectedValue,
  onChange,
}: {
  ariaLabel: string;
  testId: string;
  options: { label: string; value: string }[];
  selectedValue: string;
  onChange: (next: string) => void;
}) {
  const value = options.some((opt) => opt.value === selectedValue)
    ? selectedValue
    : '';

  return (
    <FormControl size="small" sx={styles.field}>
      <InputLabel shrink>Value</InputLabel>
      <Select
        displayEmpty
        label="Value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': ariaLabel }}
        SelectDisplayProps={
          { 'data-testid': testId } as React.HTMLAttributes<HTMLDivElement>
        }
        renderValue={(selected) =>
          selected ? (
            (options.find((opt) => opt.value === selected)?.label ?? selected)
          ) : (
            <Typography component="span" sx={styles.placeholderText}>
              Select a value
            </Typography>
          )
        }
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function clearColumn(
  value: DisclosureFilterValue,
  column: DisclosureFilterColumn,
): DisclosureFilterValue {
  if (column === 'submittedDate') {
    const next = { ...value };
    delete next.submittedDate;
    return next;
  }
  if (column === 'customField') {
    const next = { ...value };
    delete next.customField;
    return next;
  }
  return { ...value, [column]: [] };
}
